import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomer } from '@/hooks/useCustomer';
import { useCustomerVehicles } from '@/hooks/useCustomerVehicles';
import { useServiceRecommendations } from '@/hooks/useServiceRecommendations';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ArrowLeft, 
  User, 
  Phone, 
  Mail, 
  Car, 
  FileText, 
  AlertTriangle,
  CheckCircle2,
  Clock,
  Calendar,
  Plus
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

export default function CustomerProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeShop } = useAuth();
  // OD-34: technicians and detail specialists read this profile; creating a ticket stays with the front office.
  const { canCreateTickets } = usePermissions();

  const { customer, isLoading: isLoadingCustomer } = useCustomer(id);
  const { data: vehicles, isLoading: isLoadingVehicles } = useCustomerVehicles(id);
  const { data: recommendations, isLoading: isLoadingRecs } = useServiceRecommendations(id);

  // Fetch ticket history
  const { data: tickets, isLoading: isLoadingTickets } = useQuery({
    queryKey: ['customer-tickets', id, activeShop?.id],
    queryFn: async () => {
      if (!id || !activeShop?.id) return [];
      
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          id,
          status,
          type,
          customer_name,
          scheduled_date,
          created_at,
          predicted_minutes,
          vehicle:vehicles(id, year, make, model)
        `)
        .eq('shop_id', activeShop.id)
        .eq('customer_id', id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data || [];
    },
    enabled: !!id && !!activeShop?.id,
  });

  if (isLoadingCustomer) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <User className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-lg font-medium mb-2">Customer Not Found</h2>
            <p className="text-muted-foreground mb-4">
              The customer you're looking for doesn't exist or you don't have access.
            </p>
            <Button onClick={() => navigate('/customers')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Customers
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeRecs = recommendations?.filter(r => r.status === 'recommended') || [];
  const pendingRecs = recommendations?.filter(r => r.status === 'approved') || [];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/customers')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{customer.full_name}</h1>
          <p className="text-muted-foreground">Customer Profile</p>
        </div>
        {canCreateTickets && (
          <Button onClick={() => navigate('/create-ticket')} data-testid="customer-profile-new-ticket">
            <Plus className="h-4 w-4 mr-2" />
            New Ticket
          </Button>
        )}
      </div>

      {/* Contact Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5" />
            Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {customer.phone && (
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Phone className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Phone</div>
                  <div className="font-medium">{customer.phone}</div>
                </div>
              </div>
            )}
            {customer.email && (
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Email</div>
                  <div className="font-medium">{customer.email}</div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-sm text-muted-foreground">Customer Since</div>
                <div className="font-medium">
                  {format(new Date(customer.created_at), 'MMM d, yyyy')}
                </div>
              </div>
            </div>
          </div>
          
          {/* Communication Preferences */}
          <div className="mt-4 flex items-center gap-2">
            {customer.sms_opt_in && (
              <Badge variant="outline" className="text-xs">
                <CheckCircle2 className="h-3 w-3 mr-1 text-green-500" />
                SMS OK
              </Badge>
            )}
            {customer.email_opt_in && (
              <Badge variant="outline" className="text-xs">
                <CheckCircle2 className="h-3 w-3 mr-1 text-green-500" />
                Email OK
              </Badge>
            )}
            {customer.preferred_channel && (
              <Badge variant="secondary" className="text-xs">
                Preferred: {customer.preferred_channel}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Vehicles, History, Recommendations */}
      <Tabs defaultValue="vehicles" className="space-y-4">
        <TabsList>
          <TabsTrigger value="vehicles" className="gap-2">
            <Car className="h-4 w-4" />
            Vehicles ({vehicles?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <FileText className="h-4 w-4" />
            History ({tickets?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="recommendations" className="gap-2">
            <AlertTriangle className="h-4 w-4" />
            Recommendations ({activeRecs.length})
          </TabsTrigger>
        </TabsList>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Vehicles</CardTitle>
              <CardDescription>All vehicles associated with this customer</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingVehicles ? (
                <div className="space-y-3">
                  {[1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : vehicles?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No vehicles linked to this customer yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {vehicles?.map((vehicle) => (
                    <div
                      key={vehicle.id}
                      className="p-4 border rounded-lg flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                          <Car className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </div>
                          <div className="text-sm text-muted-foreground flex gap-3">
                            {vehicle.vin && <span>VIN: {vehicle.vin.slice(-8)}</span>}
                            {vehicle.license_plate && <span>Plate: {vehicle.license_plate}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Service History</CardTitle>
              <CardDescription>Past and current tickets for this customer</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingTickets ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : tickets?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No service history yet.
                </p>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {tickets?.map((ticket) => {
                      const vehicle = ticket.vehicle as { id: string; year: number; make: string; model: string } | null;
                      return (
                        <div
                          key={ticket.id}
                          className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => navigate('/')} // Could navigate to ticket detail
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant={ticket.status === 'Active' ? 'default' : 'secondary'}>
                                {ticket.status}
                              </Badge>
                              <Badge variant="outline">{ticket.type}</Badge>
                            </div>
                            <span className="text-sm text-muted-foreground">
                              {ticket.id}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            {vehicle && (
                              <span className="flex items-center gap-1">
                                <Car className="h-3 w-3" />
                                {vehicle.year} {vehicle.make} {vehicle.model}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {ticket.scheduled_date 
                                ? format(parseISO(ticket.scheduled_date + 'T00:00:00'), 'MMM d, yyyy')
                                : format(new Date(ticket.created_at), 'MMM d, yyyy')
                              }
                            </span>
                            {ticket.predicted_minutes && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {ticket.predicted_minutes} min
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Service Recommendations</CardTitle>
              <CardDescription>Recommended work that hasn't been performed yet</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingRecs ? (
                <div className="space-y-3">
                  {[1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : activeRecs.length === 0 && pendingRecs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No outstanding recommendations.
                </p>
              ) : (
                <div className="space-y-4">
                  {activeRecs.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 text-amber-600">Recommended</h4>
                      <div className="space-y-2">
                        {activeRecs.map((rec) => (
                          <div
                            key={rec.id}
                            className="p-4 border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 rounded-lg"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="font-medium">{rec.title}</div>
                                {rec.description && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {rec.description}
                                  </p>
                                )}
                              </div>
                              <Badge 
                                variant={rec.severity === 'urgent' ? 'destructive' : rec.severity === 'soon' ? 'default' : 'secondary'}
                              >
                                {rec.severity}
                              </Badge>
                            </div>
                            <div className="mt-2 text-xs text-muted-foreground">
                              Recommended {format(new Date(rec.recommended_at), 'MMM d, yyyy')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {pendingRecs.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 text-blue-600">Approved (Pending)</h4>
                      <div className="space-y-2">
                        {pendingRecs.map((rec) => (
                          <div
                            key={rec.id}
                            className="p-4 border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 rounded-lg"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="font-medium">{rec.title}</div>
                                {rec.description && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {rec.description}
                                  </p>
                                )}
                              </div>
                              <Badge variant="outline">Approved</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
